import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { aideTitre } from "./reglesSuiviLogs";
import "./SuiviLogs.css";

import {
  DownloadOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  CheckOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import axios from "../../utils/axios";
import PageEntete, { Puces, useUrlTab } from "../utils/PageEntete";
import IconButton from "../utils/IconButton";
import moment from "moment";
import { useHistory } from "react-router-dom";

const { RangePicker } = DatePicker;

export const STATUTS_SUIVI = [
  { value: "a_traiter", label: "À traiter", color: "red" },
  { value: "pris_en_charge", label: "Pris en charge", color: "orange" },
  { value: "resolu", label: "Résolu", color: "green" },
];
export const COMMENTAIRE_MAX = 1000;
const PAGE_SIZE = 50;
export const COULEURS = { ERREUR: "#ff4d4f", WARNING: "#fa8c16", GRISES: "#8c8c8c" };
const BASE = "/api/logs-monitoring";

const fmt = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm") : "-");
const errMsg = (e) =>
  e?.response?.data?.error || e?.message || "Erreur inattendue";

export const FORMAT_BORNE = "YYYY-MM-DDTHH:mm";
export const FORMAT_AFFICHAGE_BORNE = "DD/MM/YYYY HH:mm";
export const EXPORT_URL = `${BASE}/export`;
export const EXPORT_TITRE = "Exporter la sélection en CSV ?";
export const EXPORT_AVERTISSEMENT =
  "Un export volumineux peut prendre du temps. Toutes les pages et les messages de log des builds sont inclus dans le fichier.";
const SLUGS_ONGLET = { ERREUR: "erreurs", WARNING: "warnings", GRISES: "grises" };

// Bornes de la plage envoyées à l'API (YYYY-MM-DDTHH:mm) ; une borne seule est envoyée.
// Les heures par défaut (00:00 / 23:59) sont posées par le sélecteur (showTime.defaultValue).
export const rangeToParams = (range) => {
  const params = {};
  const [debut, fin] = Array.isArray(range) ? range : [];
  if (debut && moment.isMoment(debut) && debut.isValid()) {
    params.from = debut.format(FORMAT_BORNE);
  }
  if (fin && moment.isMoment(fin) && fin.isValid()) {
    params.to = fin.format(FORMAT_BORNE);
  }
  return params;
};

// Paramètres de l'export : filtres appliqués (pas les saisies en cours).
export const buildExportParams = (tab, applied, traitement, heures) => {
  if (tab === "GRISES") {
    const params = { status: "GRISES", hours: heures };
    if (traitement) params.job = traitement;
    return params;
  }
  const params = { status: tab };
  if (applied.hostname) params.hostname = applied.hostname;
  if (applied.env) params.env = applied.env;
  if (traitement) params.job = traitement;
  if (applied.statut_suivi) params.follow_up_status = applied.statut_suivi;
  return { ...params, ...rangeToParams(applied.range) };
};

// Paramètres des compteurs d'onglets (/summary) : filtres appliqués + traitement + seuil grisés.
// Seuls les paramètres non vides sont envoyés ; jamais de `statut`.
export const buildSummaryParams = (applied, traitement, heures) => {
  const a = applied || {};
  const params = {};
  if (a.hostname) params.hostname = a.hostname;
  if (a.env) params.env = a.env;
  if (a.statut_suivi) params.follow_up_status = a.statut_suivi;
  if (traitement) params.job = traitement;
  if (heures) params.hours = heures;
  return { ...params, ...rangeToParams(a.range) };
};

// --- Regroupement par traitement (BI-675) ---
export const GROUP_BY_URL = `${BASE}/by-job`;
// Zone « Regrouper » : une entrée de liste = une case (l'état est un tableau de clés).
export const GROUPEMENTS = [{ value: "traitement", label: "Regrouper par traitement" }];
export const MSG_RESOLUS =
  "Les builds résolus ne sont pas comptés dans la vue regroupée";
export const NOTE_GRISES =
  "Période et statut de suivi ne s'appliquent pas aux builds grisés.";
export const CATEGORIES = {
  ERREUR: { label: "Erreur", color: COULEURS.ERREUR },
  WARNING: { label: "Warning", color: COULEURS.WARNING },
  GRISE: { label: "Grisé", color: COULEURS.GRISES },
};

// Filtres de la vue regroupée / de son export : jamais de `statut` (croisement imposé).
export const buildGroupedParams = (applied, traitement, heures) => {
  const params = { hours: heures };
  if (applied.hostname) params.hostname = applied.hostname;
  if (applied.env) params.env = applied.env;
  if (traitement) params.job = traitement;
  if (applied.statut_suivi) params.follow_up_status = applied.statut_suivi;
  return { ...params, ...rangeToParams(applied.range) };
};

export const buildGroupedExportParams = (applied, traitement, heures) => ({
  groupBy: "job",
  ...buildGroupedParams(applied, traitement, heures),
});

// Mapping d'un build du détail (`by-job?detail=`) vers la ligne affichée.
// Seul endroit à ajuster si le backend change la forme de `detail`.
export const normalizeDetailItem = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const brut = String(r.categorie || r.statut || "").toUpperCase();
  const categorie = brut.startsWith("GRIS") ? "GRISE" : brut;
  return {
    ...r,
    categorie,
    statut: categorie === "GRISE" ? null : categorie,
    end_time: r.end_time || null,
    start_time: r.start_time || null,
    statut_suivi: categorie === "GRISE" ? null : r.statut_suivi || null,
  };
};
export const extractDetailItems = (data) => {
  const list = Array.isArray(data) ? data : data?.items || data?.builds;
  return Array.isArray(list)
    ? list.filter((x) => x && typeof x === "object").map(normalizeDetailItem)
    : [];
};

export const nomFichierExport = (tab, disposition, now = moment(), grouped = false) => {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
  if (m && m[1]) {
    try {
      return decodeURIComponent(m[1].trim());
    } catch (e) {
      return m[1].trim();
    }
  }
  if (grouped) return `suivi-logs-par-traitement-${now.format("YYYYMMDD-HHmmss")}.csv`;
  return `suivi-logs-${SLUGS_ONGLET[tab] || "export"}-${now.format("YYYYMMDD-HHmmss")}.csv`;
};

// Message d'erreur d'une réponse blob (le corps JSON est dans le blob).
const errMsgBlob = async (e) => {
  const data = e?.response?.data;
  if (data && typeof data.text === "function") {
    try {
      const j = JSON.parse(await data.text());
      if (j && j.error) return j.error;
    } catch (x) {
      // corps non JSON
    }
    return e?.message || "Erreur inattendue";
  }
  return errMsg(e);
};

export const buildLogsUrl = (b) =>
  `${BASE}/builds/${encodeURIComponent(b.no_build)}/${encodeURIComponent(
    b.traitement_id
  )}/logs`;
export const buildPatchUrl = (b) =>
  `${BASE}/builds/${encodeURIComponent(b.no_build)}/${encodeURIComponent(
    b.traitement_id
  )}`;

export const fmtDepuis = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return "-";
  if (n <= 48) return `${n}h`;
  return `${Math.floor(n / 24)}j ${n % 24}h`;
};

export const POLL_MS = 5000;
export const CONSOLIDATE_TITRE = "Lancer la consolidation maintenant ?";
export const CONSOLIDATE_AVERTISSEMENT =
  "La consolidation s'exécute normalement chaque jour à 15h. La lancer manuellement lit les builds terminés dans les tables de logs et peut durer plusieurs minutes (charge sur la base, possible lenteur de l'application et des autres écrans de logs pendant le traitement). Les builds déjà consolidés ne sont pas retraités : un relancement n'apporte que les builds terminés depuis le dernier passage. Ne le relancez pas en boucle.";

const fmtHeure = (d) => (d ? moment(d).format("HH:mm") : "");
export const fmtDuree = (s) => {
  const n = Math.round(Number(s));
  if (!Number.isFinite(n) || n < 0) return "-";
  if (n < 60) return `${n}s`;
  return `${Math.floor(n / 60)}min ${n % 60}s`;
};

// Texte d'état du dernier passage de la consolidation.
export const texteDernierPassage = (run) => {
  if (!run || typeof run !== "object") return "Aucun passage enregistré";
  const quand = fmt(run.ended_at || run.started_at);
  const detail = [
    run.trigger === "manual" ? "manuel" : "cron",
    `${Number(run.nb_builds) || 0} builds`,
    fmtDuree(run.duration_s),
  ].join(", ");
  if (run.status === "error") return `Dernier passage en erreur : ${quand} (${detail})`;
  if (run.status === "skipped") return `Dernier passage ignoré : ${quand} (${detail})`;
  return `Dernier passage : ${quand} (${detail})`;
};

export const MSG_PAS_DE_MAP = "Pas de correspondance sur la map";

// Statut affiche, dans l'URL (?status=, valeurs en anglais) -> categorie de l'API.
const STATUTS_URL = { errors: "ERREUR", warnings: "WARNING", greyed: "GRISES" };

// URL de la map pour une ligne ; null si group/server/env manquant.
export const buildMapUrl = (b) => {
  const segs = [b && b.group_name, b && b.server_name, b && b.code_client_cti];
  if (segs.some((s) => s === null || s === undefined || String(s) === "")) {
    return null;
  }
  return `/map/${segs
    .map((s) => encodeURIComponent(String(s).toLowerCase()))
    .join("/")}`;
};

export function MapLink({ row }) {
  const history = useHistory();
  const url = buildMapUrl(row);
  if (!url) {
    return (
      <Tooltip title={MSG_PAS_DE_MAP}>
        <span
          role="link"
          aria-disabled="true"
          aria-label="Voir sur la map"
          tabIndex={0}
          style={{ color: "#bfbfbf", cursor: "not-allowed", whiteSpace: "nowrap" }}
        >
          <EnvironmentOutlined /> Map
        </span>
      </Tooltip>
    );
  }
  return (
    <a
      href={url}
      aria-label="Voir sur la map"
      title="Voir sur la map"
      style={{ whiteSpace: "nowrap" }}
      onClick={(e) => {
        if (history && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button)) {
          e.preventDefault();
          history.push(url);
        }
      }}
    >
      <EnvironmentOutlined /> Map
    </a>
  );
}

function SuiviTag({ value }) {
  const s = STATUTS_SUIVI.find((x) => x.value === value);
  return <Tag color={s ? s.color : "default"}>{s ? s.label : value || "-"}</Tag>;
}

function SuiviLogs() {
  const [statusKey, setStatusKey] = useUrlTab(Object.keys(STATUTS_URL), "status");
  const tab = STATUTS_URL[statusKey];
  const setTab = (t) =>
    setStatusKey(Object.keys(STATUTS_URL).find((k) => STATUTS_URL[k] === t));
  const [showRegles, setShowRegles] = useState(false);
  const [summary, setSummary] = useState({ erreurs: 0, warnings: 0, grises: 0 });
  const [filters, setFilters] = useState({
    hostname: "",
    env: "",
    statut_suivi: undefined,
    range: null,
  });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [heures, setHeures] = useState(24);
  const [silent, setSilent] = useState([]);
  const [traitement, setTraitement] = useState(undefined);
  const [traitements, setTraitements] = useState([]);
  const [seuilServi, setSeuilServi] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailState, setDetailState] = useState({
    loading: false,
    error: null,
    items: [],
  });
  const [ack, setAck] = useState(null);
  const [ackSaving, setAckSaving] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [form] = Form.useForm();
  const [cons, setCons] = useState({ running: false, lastRun: null, nextAllowedAt: null });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [groupBy, setGroupBy] = useState([]);
  const [appliedHeures, setAppliedHeures] = useState(24);
  const [grouped, setGrouped] = useState({ items: [], loaded: false });
  const [groupDetails, setGroupDetails] = useState({});
  const [, setTick] = useState(0);
  const wasRunning = useRef(false);

  const summaryReq = useRef(0);
  const loadSummary = useCallback(async () => {
    const reqId = ++summaryReq.current;
    try {
      const res = await axios.get(`${BASE}/summary`, {
        params: buildSummaryParams(applied, traitement, heures),
      });
      if (reqId !== summaryReq.current) return; // réponse périmée
      const d = res.data || {};
      setSummary({
        erreurs: Number(d.erreurs) || 0,
        warnings: Number(d.warnings) || 0,
        grises: Number(d.grises) || 0,
      });
    } catch (e) {
      // compteurs non bloquants
    }
  }, [applied, traitement, heures]);

  const loadBuilds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { status: tab, page, limit: PAGE_SIZE };
      if (applied.hostname) params.hostname = applied.hostname;
      if (applied.env) params.env = applied.env;
      if (traitement) params.job = traitement;
      if (applied.statut_suivi) params.follow_up_status = applied.statut_suivi;
      Object.assign(params, rangeToParams(applied.range));
      const res = await axios.get(`${BASE}/builds`, { params });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setData({ items, total: Number(res.data?.total) || 0 });
    } catch (e) {
      setError(errMsg(e));
      setData({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [tab, page, applied, traitement]);

  const groupedOn = groupBy.includes("traitement");

  const loadGrouped = useCallback(async () => {
    setLoading(true);
    setError(null);
    setGroupDetails({});
    try {
      const res = await axios.get(GROUP_BY_URL, {
        params: buildGroupedParams(applied, traitement, appliedHeures),
      });
      const items = Array.isArray(res.data?.items)
        ? res.data.items.filter((i) => i && typeof i.traitement === "string")
        : [];
      setGrouped({ items, loaded: true });
      const s = Number(res.data?.silenceHours);
      if (Number.isFinite(s) && s > 0) setSeuilServi(s);
    } catch (e) {
      setError(errMsg(e));
      setGrouped({ items: [], loaded: true });
    } finally {
      setLoading(false);
    }
  }, [applied, traitement, appliedHeures]);

  const loadGroupDetail = async (nom) => {
    setGroupDetails((d) => ({ ...d, [nom]: { loading: true, error: null, items: [] } }));
    try {
      const res = await axios.get(GROUP_BY_URL, {
        params: { ...buildGroupedParams(applied, traitement, appliedHeures), detail: nom },
      });
      setGroupDetails((d) => ({
        ...d,
        [nom]: { loading: false, error: null, items: extractDetailItems(res.data) },
      }));
    } catch (e) {
      setGroupDetails((d) => ({
        ...d,
        [nom]: { loading: false, error: errMsg(e), items: [] },
      }));
    }
  };

  const loadSilent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE}/silent`, { params: { hours: heures } });
      setSilent(Array.isArray(res.data?.items) ? res.data.items : []);
      // seuil réel : valeur servie par l'API si exposée, sinon celle envoyée
      const s = Number(res.data?.silenceHours ?? res.data?.heures);
      setSeuilServi(Number.isFinite(s) && s > 0 ? s : heures);
    } catch (e) {
      setError(errMsg(e));
      setSilent([]);
    } finally {
      setLoading(false);
    }
  }, [heures]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadTraitements = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE}/jobs`);
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      if (alive.current) {
        setTraitements(
          items.map((i) => i && i.name).filter((n) => typeof n === "string" && n)
        );
      }
    } catch (e) {
      // filtre non bloquant
    }
  }, []);

  useEffect(() => {
    loadTraitements();
  }, [loadTraitements]);

  useEffect(() => {
    if (groupedOn) {
      loadGrouped();
    } else if (tab === "GRISES") {
      loadSilent();
    } else {
      loadBuilds();
    }
  }, [groupedOn, tab, loadGrouped, loadBuilds, loadSilent]);

  // --- Consolidation manuelle (BI-671) ---
  const applyStatus = useCallback((st) => {
    if (!st || typeof st !== "object") return null;
    const next = {
      running: !!st.running,
      lastRun: st.lastRun && typeof st.lastRun === "object" ? st.lastRun : null,
      nextAllowedAt: st.nextAllowedAt || null,
    };
    if (alive.current) setCons(next);
    return next;
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE}/consolidate/status`);
      return applyStatus(res.data);
    } catch (e) {
      return null; // état non bloquant
    }
  }, [applyStatus]);

  const refreshAll = () => {
    loadSummary();
    loadTraitements();
    if (groupedOn) loadGrouped();
    else if (tab === "GRISES") loadSilent();
    else loadBuilds();
  };
  const refreshRef = useRef(refreshAll);
  refreshRef.current = refreshAll;

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Sondage tant qu'une consolidation tourne ; fin = rechargement + message.
  useEffect(() => {
    if (cons.running) {
      wasRunning.current = true;
      const id = setInterval(loadStatus, POLL_MS);
      return () => clearInterval(id);
    }
    if (wasRunning.current) {
      wasRunning.current = false;
      refreshRef.current();
      if (cons.lastRun && cons.lastRun.status === "error") {
        message.error("La consolidation s'est terminée en erreur.");
      } else {
        message.success("Consolidation terminée.");
      }
    }
    return undefined;
  }, [cons.running, cons.lastRun, loadStatus]);

  // Réactive le bouton à l'expiration de nextAllowedAt.
  useEffect(() => {
    if (!cons.nextAllowedAt) return undefined;
    const ms = moment(cons.nextAllowedAt).diff(moment());
    if (!(ms > 0)) return undefined;
    const id = setTimeout(() => setTick((t) => t + 1), ms + 50);
    return () => clearTimeout(id);
  }, [cons.nextAllowedAt]);

  const launchConsolidation = async () => {
    setLaunching(true);
    try {
      const res = await axios.post(`${BASE}/consolidate`);
      setConfirmOpen(false);
      const st = applyStatus(res.data?.status);
      if (!st || !st.running) {
        setCons((c) => ({ ...c, running: true }));
      }
      message.info("Consolidation lancée.");
    } catch (e) {
      setConfirmOpen(false);
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      applyStatus(data.status);
      if (status === 409) {
        message.warning(data.error || "Une consolidation est déjà en cours.");
      } else if (status === 429) {
        const h = fmtHeure(data.retryAfter);
        message.warning(
          h
            ? `Consolidation trop récente : relance possible à ${h}.`
            : data.error || "Consolidation trop récente."
        );
      } else {
        message.error(errMsg(e));
      }
    } finally {
      if (alive.current) setLaunching(false);
    }
  };

  const exportCsv = async () => {
    const onglet = tab;
    const regroupe = groupedOn;
    setExporting(true);
    try {
      const res = await axios.get(EXPORT_URL, {
        params: regroupe
          ? buildGroupedExportParams(applied, traitement, appliedHeures)
          : buildExportParams(onglet, applied, traitement, heures),
        responseType: "blob",
      });
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data || ""], { type: "text/csv;charset=UTF-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomFichierExport(
        onglet,
        res.headers?.["content-disposition"],
        moment(),
        regroupe
      );
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
      if (alive.current) setExportOpen(false);
    } catch (e) {
      const msg = await errMsgBlob(e);
      if (alive.current) setExportOpen(false);
      message.error(`Export impossible : ${msg}`);
    } finally {
      if (alive.current) setExporting(false);
    }
  };

  const openDetail = async (b) => {
    setDetail(b);
    setDetailState({ loading: true, error: null, items: [] });
    try {
      const res = await axios.get(buildLogsUrl(b), {
        params: {
          hostname: b.hostname,
          env: b.env,
          level: b.statut === "WARNING" ? "WARNING" : "ERROR",
        },
      });
      setDetailState({
        loading: false,
        error: null,
        items: Array.isArray(res.data?.items) ? res.data.items : [],
      });
    } catch (e) {
      setDetailState({ loading: false, error: errMsg(e), items: [] });
    }
  };

  const openAck = (b) => {
    setAck(b);
    setAckError(null);
    form.setFieldsValue({
      statut_suivi: b.statut_suivi || "a_traiter",
      commentaire: b.commentaire || "",
    });
  };

  const submitAck = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setAckSaving(true);
    setAckError(null);
    try {
      await axios.patch(buildPatchUrl(ack), {
        hostname: ack.hostname,
        env: ack.env,
        statut_suivi: values.statut_suivi,
        commentaire: values.commentaire || "",
      });
      setAck(null);
      if (groupedOn) loadGrouped();
      else loadBuilds();
      loadSummary();
    } catch (e) {
      setAckError(errMsg(e));
    } finally {
      setAckSaving(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
    setApplied(filters);
    setAppliedHeures(heures);
  };

  const buildColumns = [
    { title: "Client", dataIndex: "hostname", key: "hostname" },
    { title: "Env", dataIndex: "env", key: "env" },
    { title: "Build", dataIndex: "no_build", key: "no_build" },
    {
      title: "Traitement",
      dataIndex: "traitement_name",
      key: "traitement",
      render: (v, r) => v || r.traitement_id,
    },
    { title: "Fin", dataIndex: "end_time", key: "end_time", render: fmt },
    {
      title: "Niveau",
      dataIndex: "statut",
      key: "statut",
      render: (v) =>
        v ? <Tag color={COULEURS[v] || "default"}>{v}</Tag> : "-",
    },
    { title: "Erreurs", dataIndex: "nb_erreurs", key: "nb_erreurs" },
    { title: "Warnings", dataIndex: "nb_warnings", key: "nb_warnings" },
    {
      title: "Suivi",
      dataIndex: "statut_suivi",
      key: "statut_suivi",
      render: (v) => <SuiviTag value={v} />,
    },
    {
      title: "Commentaire",
      dataIndex: "commentaire",
      key: "commentaire",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, b) => (
        <Space wrap={false}>
          <IconButton
            size="small"
            title="Voir les logs"
            label="Logs"
            icon={<FileSearchOutlined />}
            onClick={() => openDetail(b)}
          />
          <IconButton
            size="small"
            type="primary"
            title="Acquitter"
            icon={<CheckOutlined />}
            onClick={() => openAck(b)}
          />
          <MapLink row={b} />
        </Space>
      ),
    },
  ];

  const groupedColumns = [
    { title: "Traitement", dataIndex: "traitement", key: "traitement" },
    { title: "Score", dataIndex: "score", key: "score" },
    { title: "Erreurs", dataIndex: "nb_erreurs", key: "nb_erreurs" },
    { title: "Warnings", dataIndex: "nb_warnings", key: "nb_warnings" },
    { title: "Grisés", dataIndex: "nb_grises", key: "nb_grises" },
    {
      title: "Messages d'erreur distincts",
      dataIndex: "msg_erreurs_distincts",
      key: "msg_erreurs_distincts",
    },
    {
      title: "Messages de warning distincts",
      dataIndex: "msg_warnings_distincts",
      key: "msg_warnings_distincts",
    },
  ];

  const groupedDetailColumns = [
    {
      title: "Catégorie",
      dataIndex: "categorie",
      key: "categorie",
      render: (v) => {
        const c = CATEGORIES[v];
        return c ? <Tag color={c.color}>{c.label}</Tag> : v || "-";
      },
    },
    { title: "Client", dataIndex: "hostname", key: "hostname" },
    { title: "Env", dataIndex: "env", key: "env" },
    { title: "Build", dataIndex: "no_build", key: "no_build" },
    {
      title: "Fin / Début",
      key: "date",
      render: (_, r) => fmt(r.categorie === "GRISE" ? r.start_time : r.end_time),
    },
    {
      title: "Statut de suivi",
      dataIndex: "statut_suivi",
      key: "statut_suivi",
      render: (v, r) => (r.categorie === "GRISE" || !v ? "" : <SuiviTag value={v} />),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, b) => (
        <Space wrap={false}>
          {b.categorie !== "GRISE" && (
            <IconButton
              size="small"
              title="Voir les logs"
              label="Logs"
              icon={<FileSearchOutlined />}
              onClick={() => openDetail(b)}
            />
          )}
          {b.categorie !== "GRISE" && (
            <IconButton
              size="small"
              type="primary"
              title="Acquitter"
              icon={<CheckOutlined />}
              onClick={() => openAck(b)}
            />
          )}
          <MapLink row={b} />
        </Space>
      ),
    },
  ];

  const silentColumns = [
    { title: "Build", dataIndex: "no_build", key: "no_build" },
    {
      title: "Traitement",
      dataIndex: "traitement_name",
      key: "traitement",
      render: (v, r) => v || r.traitement_id,
    },
    {
      title: "Client",
      dataIndex: "hostname",
      key: "hostname",
      render: (v) => v || "\u2014",
    },
    { title: "Env", dataIndex: "env", key: "env" },
    {
      title: "Démarré le",
      dataIndex: "start_time",
      key: "start_time",
      render: fmt,
    },
    {
      title: "Sans réponse depuis",
      dataIndex: "depuis_heures",
      key: "depuis_heures",
      render: (h) => fmtDepuis(h),
    },
    {
      title: "Map",
      key: "map",
      render: (_, r) => <MapLink row={r} />,
    },
  ];

  const logColumns = [
    { title: "Date", dataIndex: "timestamp", key: "timestamp", render: fmt },
    { title: "Niveau", dataIndex: "level", key: "level" },
    { title: "Fichier", dataIndex: "filename", key: "filename", ellipsis: true },
    { title: "Message", dataIndex: "message", key: "message" },
  ];

  const relanceTitre =
    !cons.running && cons.nextAllowedAt && moment(cons.nextAllowedAt).isAfter(moment())
      ? `Relance possible à ${fmtHeure(cons.nextAllowedAt)}`
      : null;

  const seuilAffiche = seuilServi || heures;
  const regles = aideTitre(seuilAffiche);

  const tabLabel = (label, n, couleur) => (
    <span>
      {label}{" "}
      <Badge
        count={n}
        overflowCount={9999}
        showZero
        style={{ backgroundColor: couleur }}
      />
    </span>
  );

  const silentFiltre = traitement
    ? silent.filter((r) => r.traitement_name === traitement)
    : silent;

  const totalExport = groupedOn
    ? grouped.items.length
    : tab === "GRISES"
    ? silentFiltre.length
    : data.total;
  const uniteExport = groupedOn ? "traitements" : "builds";

  const filtreTraitement = (
    <Select
      showSearch
      allowClear
      placeholder="Traitement"
      aria-label="Filtre traitement"
      style={{ width: 220 }}
      value={traitement}
      onChange={(v) => {
        setTraitement(v || undefined);
        setPage(1);
      }}
      options={traitements.map((n) => ({ value: n, label: n }))}
    />
  );

  const emptyText = error ? "Aucune donnée" : "Aucun résultat";

  const content = (
    <>
      {tab === "GRISES" && !groupedOn ? (
        <Space style={{ marginBottom: 12 }}>
          <span>Sans réponse depuis (heures)</span>
          <InputNumber
            aria-label="Seuil en heures"
            min={1}
            max={720}
            value={heures}
            onChange={(v) => setHeures(v || 24)}
          />
          {filtreTraitement}
        </Space>
      ) : (
        <Space wrap style={{ marginBottom: 12 }}>
          <Input
            placeholder="Client (hostname)"
            allowClear
            value={filters.hostname}
            onChange={(e) => setFilters({ ...filters, hostname: e.target.value })}
            onPressEnter={applyFilters}
          />
          <Input
            placeholder="Env"
            allowClear
            value={filters.env}
            onChange={(e) => setFilters({ ...filters, env: e.target.value })}
            onPressEnter={applyFilters}
          />
          <RangePicker
            showTime={{
              format: "HH:mm",
              defaultValue: [moment("00:00", "HH:mm"), moment("23:59", "HH:mm")],
            }}
            format={FORMAT_AFFICHAGE_BORNE}
            allowEmpty={[true, true]}
            value={filters.range}
            onChange={(range) => setFilters({ ...filters, range })}
          />
          <Select
            placeholder="Statut de suivi"
            allowClear
            style={{ width: 170 }}
            value={filters.statut_suivi}
            onChange={(v) => setFilters({ ...filters, statut_suivi: v })}
            options={STATUTS_SUIVI.map(({ value, label }) => ({ value, label }))}
          />
          {filtreTraitement}
          {groupedOn && (
            <Space>
              <span>Sans réponse depuis (heures)</span>
              <InputNumber
                aria-label="Seuil en heures"
                min={1}
                max={720}
                value={heures}
                onChange={(v) => setHeures(v || 24)}
              />
            </Space>
          )}
          <Button type="primary" onClick={applyFilters}>
            Filtrer
          </Button>
        </Space>
      )}

      <Space style={{ marginBottom: 12, display: "flex" }} data-testid="zone-regrouper">
        <span>Regrouper :</span>
        <Checkbox.Group
          options={GROUPEMENTS}
          value={groupBy}
          onChange={(v) => setGroupBy(v)}
        />
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Erreur de chargement"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button
              size="small"
              onClick={groupedOn ? loadGrouped : tab === "GRISES" ? loadSilent : loadBuilds}
            >
              Réessayer
            </Button>
          }
        />
      )}

      {groupedOn && applied.statut_suivi === "resolu" && (
        <Alert
          type="info"
          showIcon
          message={MSG_RESOLUS}
          style={{ marginBottom: 12 }}
        />
      )}

      {groupedOn ? (
        <>
          <Table
            rowKey="traitement"
            loading={loading}
            columns={groupedColumns}
            scroll={{ x: "max-content" }}
            dataSource={grouped.items}
            pagination={false}
            locale={{ emptyText }}
            expandable={{
              onExpand: (open, r) => {
                if (open && !groupDetails[r.traitement]) loadGroupDetail(r.traitement);
              },
              expandedRowRender: (r) => {
                const d = groupDetails[r.traitement] || { loading: true, items: [] };
                return (
                  <>
                    {d.error && (
                      <Alert type="error" showIcon message={d.error} style={{ marginBottom: 8 }} />
                    )}
                    <Table
                      size="small"
                      rowKey={(b) =>
                        `${b.no_build}|${b.traitement_id}|${b.hostname}|${b.env}`
                      }
                      loading={d.loading}
                      columns={groupedDetailColumns}
                      dataSource={d.items}
                      pagination={false}
                      locale={{ emptyText: "Aucun build" }}
                    />
                  </>
                );
              },
            }}
          />
          <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            {NOTE_GRISES}
          </Typography.Text>
        </>
      ) : tab === "GRISES" ? (
        <Table
          rowKey={(r) =>
            `${r.no_build}|${r.traitement_id}`
          }
          loading={loading}
          columns={silentColumns}
          dataSource={silentFiltre}
          pagination={{ pageSize: PAGE_SIZE }}
          rowClassName={() => "suivi-logs-grise"}
          onRow={() => ({ style: { opacity: 0.55, background: "#f5f5f5" } })}
          locale={{ emptyText }}
        />
      ) : (
        <Table
          rowKey={(r) =>
            `${r.no_build}|${r.traitement_id}|${r.hostname}|${r.env}`
          }
          loading={loading}
          columns={buildColumns}
          scroll={{ x: "max-content" }}
          dataSource={data.items}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data.total,
            showSizeChanger: false,
          }}
          onChange={(p) => setPage(p.current)}
          locale={{ emptyText }}
        />
      )}
    </>
  );

  const changeTab = (k) => {
    setTab(k);
    setPage(1);
    setError(null);
  };

  const consolidationTitre =
    relanceTitre || (cons.running ? "Consolidation en cours…" : "Lancer la consolidation");

  return (
    <div className="cti-page">
      <PageEntete
        title="Suivi des logs"
        aside={
          <Button
            size="small"
            shape="round"
            className="suivi-logs-regles-btn"
            icon={<QuestionCircleOutlined />}
            onClick={() => setShowRegles(true)}
          >
            Règles
          </Button>
        }
        actions={
          <>
            <IconButton
              type="primary"
              title={consolidationTitre}
              label={cons.running ? "Consolidation en cours…" : "Lancer la consolidation"}
              icon={<SyncOutlined spin={cons.running} />}
              disabled={cons.running || !!relanceTitre}
              onClick={() => setConfirmOpen(true)}
            />
            <IconButton
              type="primary"
              title="Exporter en CSV"
              icon={<DownloadOutlined />}
              disabled={!(totalExport > 0) || exporting}
              loading={exporting}
              onClick={() => setExportOpen(true)}
            />
          </>
        }
      />
      <Puces
        label="Statut"
        value={tab}
        onChange={changeTab}
        items={[
          { key: "ERREUR", label: tabLabel("Erreurs", summary.erreurs, COULEURS.ERREUR) },
          { key: "WARNING", label: tabLabel("Warnings", summary.warnings, COULEURS.WARNING) },
          {
            key: "GRISES",
            label: tabLabel("Builds grisés", summary.grises, COULEURS.GRISES),
          },
        ]}
        extra={
          <Typography.Text
            type="secondary"
            className="cti-bar-end"
            data-testid="etat-consolidation"
          >
            {cons.running ? "Consolidation en cours…" : texteDernierPassage(cons.lastRun)}
          </Typography.Text>
        }
      />
      <div className="cti-panel" role="tabpanel">
        {content}
      </div>
      <Modal
        title={EXPORT_TITRE}
        open={exportOpen}
        onOk={exportCsv}
        onCancel={() => !exporting && setExportOpen(false)}
        confirmLoading={exporting}
        okText="Exporter"
        cancelText="Annuler"
      >
        <p>{EXPORT_AVERTISSEMENT}</p>
        <p style={{ margin: 0 }}>
          <strong>{totalExport} {uniteExport} correspondent aux filtres en cours.</strong>
        </p>
      </Modal>
      <Modal
        title={CONSOLIDATE_TITRE}
        open={confirmOpen}
        onOk={launchConsolidation}
        onCancel={() => setConfirmOpen(false)}
        confirmLoading={launching}
        okText="Lancer"
        cancelText="Annuler"
      >
        <p style={{ margin: 0 }}>{CONSOLIDATE_AVERTISSEMENT}</p>
      </Modal>
      <Modal
        title={regles.titre}
        open={showRegles}
        onCancel={() => setShowRegles(false)}
        footer={
          <Button type="primary" onClick={() => setShowRegles(false)}>
            Fermer
          </Button>
        }
      >
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {regles.regles.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </Modal>
      <Drawer
        title={
          detail ? `Logs du build ${detail.no_build} (${detail.hostname})` : ""
        }
        open={!!detail}
        width={800}
        onClose={() => setDetail(null)}
      >
        {detailState.error && (
          <Alert type="error" showIcon message={detailState.error} />
        )}
        <Table
          rowKey="oid"
          size="small"
          loading={detailState.loading}
          columns={logColumns}
          dataSource={detailState.items}
          pagination={{ pageSize: 50 }}
          locale={{ emptyText: "Aucun log" }}
        />
      </Drawer>

      <Modal
        title="Acquitter le build"
        open={!!ack}
        onOk={submitAck}
        onCancel={() => setAck(null)}
        confirmLoading={ackSaving}
        okText="Enregistrer"
        cancelText="Annuler"
        forceRender
      >
        {ackError && (
          <Alert
            type="error"
            showIcon
            message={ackError}
            style={{ marginBottom: 12 }}
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            name="statut_suivi"
            label="Statut de suivi"
            rules={[{ required: true, message: "Statut requis" }]}
          >
            <Select
              options={STATUTS_SUIVI.map(({ value, label }) => ({
                value,
                label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="commentaire"
            label="Commentaire"
            rules={[
              {
                max: COMMENTAIRE_MAX,
                message: `${COMMENTAIRE_MAX} caractères maximum`,
              },
            ]}
          >
            <Input.TextArea rows={4} showCount maxLength={COMMENTAIRE_MAX} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SuiviLogs;
